import { createContext, ReactNode, useReducer } from "react";
import { loaderReducer } from "./loaderReducer";
import { ActionType } from "./types/enums";
import { LoaderState } from "./types/interfaces";

interface LoaderProviderProps {
  children: ReactNode;
}

// État initial
const initialState: LoaderState = {
  loaders: {},
  globalLoading: false,
  activeLoadersCount: 0,
  errors: {},
  globalError: null,
};

export const LoaderContext = createContext<LoaderContextType | undefined>(undefined);

// Interface du contexte
export interface LoaderContextType extends LoaderState {
  startLoading: (key: string) => void;
  stopLoading: (key: string) => void;
  startGlobalLoading: () => void;
  stopGlobalLoading: () => void;
  isLoading: (key: string) => boolean;
  setError: (key: string, error: Error) => void;
  setGlobalError: (error: Error) => void;
  clearError: (key: string) => void;
  clearGlobalError: () => void;
  hasError: (key: string) => boolean;
  getError: (key: string) => Error | null;
}

export const LoaderProvider: React.FC<LoaderProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(loaderReducer, initialState);

  // Fonctions utilitaires pour les loaders
  const startLoading = (key: string) => {
    dispatch({ type: ActionType.START_LOADING, payload: { key } });
  };

  const stopLoading = (key: string) => {
    dispatch({ type: ActionType.STOP_LOADING, payload: { key } });
  };

  const startGlobalLoading = () => {
    dispatch({ type: ActionType.START_GLOBAL_LOADING });
  };

  const stopGlobalLoading = () => {
    dispatch({ type: ActionType.STOP_GLOBAL_LOADING });
  };

  const isLoading = (key: string): boolean => {
    return !!state.loaders[key];
  };

  // Fonctions utilitaires pour les erreurs
  const setError = (key: string, error: Error) => {
    dispatch({ type: ActionType.SET_ERROR, payload: { key, error, isGlobal: false } });
  };

  const setGlobalError = (error: Error) => {
    dispatch({ type: ActionType.SET_ERROR, payload: { key: null, error, isGlobal: true } });
  };

  const clearError = (key: string) => {
    dispatch({ type: ActionType.CLEAR_ERROR, payload: { key, isGlobal: false } });
  };

  const clearGlobalError = () => {
    dispatch({ type: ActionType.CLEAR_ERROR, payload: { key: null, isGlobal: true } });
  };

  const hasError = (key: string): boolean => {
    return !!state.errors[key];
  };

  const getError = (key: string): Error | null => {
    return state.errors[key] || null;
  };

  const value: LoaderContextType = {
    // États
    loaders: state.loaders,
    globalLoading: state.globalLoading,
    activeLoadersCount: state.activeLoadersCount,
    errors: state.errors,
    globalError: state.globalError,

    // Fonctions pour les loaders
    startLoading,
    stopLoading,
    startGlobalLoading,
    stopGlobalLoading,
    isLoading,

    // Fonctions pour les erreurs
    setError,
    setGlobalError,
    clearError,
    clearGlobalError,
    hasError,
    getError,
  };

  return (
    <LoaderContext.Provider value={value}>
      {children}
    </LoaderContext.Provider>
  );
};

